import React, { useCallback, useState } from 'react';
import { Download, FileSpreadsheet, Upload } from 'lucide-react';
import { executiveApiService } from '../../services/executiveApiService';

function downloadCsv(filename, content) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function messageTone(text, kind) {
  if (kind === 'error') return 'text-red-600';
  if (kind === 'warning') return 'text-amber-800';
  return 'text-green-700';
}

export default function ExecutiveSpreadsheetImportSection({ organizationId, disabled }) {
  const [asOfDate, setAsOfDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [fileName, setFileName] = useState('');
  const [csvText, setCsvText] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState({ text: '', kind: 'success' });
  const [errors, setErrors] = useState([]);

  const handleDownloadTemplate = useCallback(async () => {
    if (!organizationId) return;
    setBusy(true);
    setMessage({ text: '', kind: 'success' });
    setErrors([]);
    try {
      const content = await executiveApiService.downloadSpreadsheetTemplate(organizationId);
      downloadCsv('executive-kpi-import-template.csv', content);
      setMessage({
        text: 'Template downloaded. Fill in values for your external metrics only — not platform RCM KPIs.',
        kind: 'success',
      });
    } catch (e) {
      setMessage({ text: e.message || 'Failed to download template.', kind: 'error' });
    } finally {
      setBusy(false);
    }
  }, [organizationId]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setMessage({ text: '', kind: 'success' });
    setErrors([]);
    const reader = new FileReader();
    reader.onload = () => {
      setCsvText(String(reader.result || ''));
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!organizationId || !csvText.trim()) return;
    setBusy(true);
    setMessage({ text: '', kind: 'success' });
    setErrors([]);
    try {
      const result = await executiveApiService.importSpreadsheet(organizationId, {
        csvText,
        defaultAsOfDate: asOfDate || null,
      });
      if (result.errors?.length) {
        setErrors(result.errors);
      }
      const summary = `Imported ${result.snapshots_saved} snapshot(s), updated ${result.metrics_upserted} metric(s).`;
      setMessage({
        text: result.status === 'ok' ? summary : `${summary} Some rows had issues.`,
        kind: result.status === 'ok' ? 'success' : 'warning',
      });
      window.dispatchEvent(
        new CustomEvent('executive-custom-metrics-updated', { detail: { organizationId } })
      );
      setCsvText('');
      setFileName('');
    } catch (e) {
      setMessage({ text: e.message || 'Import failed.', kind: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
          <FileSpreadsheet className="w-5 h-5 text-blue-600" />
          Daily spreadsheet import
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Download a CSV template pre-filled with your external custom metrics, enter daily values, and
          import to save historic snapshots for trend charts. Use catalog metric_id values for integration
          top KPIs (net_revenue, operating_margin, encounters, cash_on_hand, patient_retention). Platform
          RCM metrics (net collection rate, days in A/R, denial rate) are computed live — do not import those.
        </p>
      </div>

      {disabled && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Select an organization to import spreadsheet data.
        </p>
      )}

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
        <div className="flex flex-wrap gap-3 items-end">
          <button
            type="button"
            onClick={handleDownloadTemplate}
            disabled={disabled || busy}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            Download template
          </button>
          <label className="text-xs text-gray-600">
            Default as-of date (when rows omit date)
            <input
              type="date"
              value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
              disabled={disabled || busy}
              className="mt-1 block rounded-md border border-gray-300 text-sm px-2 py-1.5"
            />
          </label>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Upload completed spreadsheet</label>
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={handleFileChange}
            disabled={disabled || busy}
            className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 file:font-medium hover:file:bg-blue-100"
          />
          {fileName && <p className="text-xs text-gray-500 mt-1">Selected: {fileName}</p>}
        </div>

        <button
          type="button"
          onClick={handleImport}
          disabled={disabled || busy || !csvText.trim()}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <Upload className="w-4 h-4" />
          {busy ? 'Importing…' : 'Import & save to history'}
        </button>

        {message.text && (
          <p className={`text-sm ${messageTone(message.text, message.kind)}`}>
            {message.text}
          </p>
        )}
        {errors.length > 0 && (
          <ul className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 list-disc pl-5 space-y-1">
            {errors.map((err) => (
              <li key={err}>{err}</li>
            ))}
          </ul>
        )}
      </div>

      <div className="text-xs text-gray-500 space-y-1">
        <p className="font-medium text-gray-600">Template columns</p>
        <p>
          metric_id, label, value, compare_value, unit, placement, scorecard_id, as_of_date, source_label
        </p>
        <p>
          Re-import each day with a new as_of_date to build trends. Rows without a value update metric
          definitions only.
        </p>
      </div>
    </div>
  );
}
